import { useState, useEffect, useRef, useMemo } from "react";
import { Link, useLocation } from "@/lib/router-compat";
import {
  PhoneIcon as Phone,
  MenuIcon as Menu,
  XIcon as X,
  ChevronDownIcon as ChevronDown,
  MessageCircleIcon as MessageCircle,
  LogInIcon as LogIn,
  LogOutIcon as LogOut,
  UserIcon as User,
} from "@/components/icons/InlineIcons";
import { Heart, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCustomerAuth } from "@/hooks/useCustomerAuth";
import { useQuery } from "@tanstack/react-query";
import { getSupabase } from "@/integrations/supabase/lazy";
import { lazy, Suspense } from "react";
import { toWebP, STATIC_ASSETS, cdnImg, renderUrl } from "@/lib/imageUtils";
import ResilientImg from "@/components/ui/ResilientImg";
import AnnouncementBanner from "./AnnouncementBanner";
import { HeaderSearchInline, HeaderSearchMobile } from "./HeaderSearch";
const NotificationDropdown = lazy(() => import("./NotificationDropdown"));
import { useInteractionReady } from "@/components/ui/InteractionGate";
import { hardRefresh } from "@/lib/hardRefresh";
import { sanitizeHtml } from "@/lib/sanitizeHtml";

function HardRefresh() {
  return (
    <button
      type="button"
      onClick={() => hardRefresh()}
      title="Force refresh — clears cache and reloads"
      aria-label="Force refresh"
      className="flex items-center gap-1 hover:underline text-xs"
    >
      <RefreshCw className="h-3 w-3" />
      <span className="hidden sm:inline">Refresh</span>
    </button>
  );
}


// Hardcoded fallback used only while DB menu loads
const fallbackNavItems = [
  {
    label: "RVs For Sale",
    children: [
      { label: "New RVs", href: "/new-rvs" },
      { label: "Used RVs", href: "/used-rvs" },
      { label: "Travel Trailers", href: "/travel-trailers" },
      { label: "Motorhomes", href: "/motorhomes" },
      { label: "Pop-Up Campers", href: "/pop-up-campers" },
      { label: "RV Specials", href: "/rv-search?special=manager" },
      { label: "Shop by Manufacturer", href: "/rv-search" },
      { label: "Tow Guides", href: "/tow-guides" },
    ],
  },
  { label: "Rentals", href: "/rentals" },
  { label: "Sell Your RV", href: "/sell-your-rv" },
  { label: "Trade-Ins", href: "/contact-us-trade-ins" },
  {
    label: "Resources",
    children: [
      { label: "Blog", href: "/blog" },
      { label: "Video Gallery", href: "/video-gallery" },
      { label: "Community Forum", href: "/forum" },
      { label: "Camping Near Us", href: "/camping-in-wilmington-north-carolina" },
      { label: "Tow Guides", href: "/tow-guides" },
      { label: "Why Buy an RV?", href: "/why-buy-an-rv" },
      { label: "Route 66 RV Network", href: "/route-66-rv-network" },
      { label: "Camping Club", href: "/camping-club" },
      { label: "Loyalty & Rewards", href: "/loyalty-program" },
      { label: "Careers", href: "/application-career" },
    ],
  },
  {
    label: "Financing",
    children: [
      { label: "Financing", href: "/rv-financing" },
      { label: "Payment Calculator", href: "/rv-financing" },
    ],
  },
  {
    label: "Parts & Service",
    children: [
      { label: "RV Service", href: "/rv-service" },
      { label: "RV Parts", href: "/rv-parts" },
      { label: "RV Component Manuals", href: "/rv-manuals" },
      { label: "RV Pre-Trip Checklist", href: "/rv-pre-trip-check-list" },
      { label: "RV Roof Seal Guide", href: "/rv-how-to-check-your-roof-seals-1" },
      { label: "How to Winterize Your RV", href: "/rv-how-to-winterize-my-rv" },
      { label: "RV Maintenance Tips", href: "/blog" },
    ],
  },
  {
    label: "About Us",
    children: [
      { label: "About Us", href: "/about" },
      { label: "Testimonials", href: "/testimonials" },
      { label: "Route 66 Network", href: "/about" },
    ],
  },
  { label: "Contact Us", href: "/contact" },
];

type NavChild = { label: string; href: string; action?: string };
type NavItem = { label: string; href?: string; action?: string; children?: NavChild[] };

/** Check if a menu item is visible to the current user */
function isItemVisible(item: any, userId?: string, userRoles?: string[]): boolean {
  const requiredRoles: string[] = item.required_roles ?? [];
  const visibleToUsers: string[] = item.visible_to_users ?? [];
  const authRequired: boolean = item.auth_required ?? false;

  // If no restrictions, visible to all
  if (!authRequired && requiredRoles.length === 0 && visibleToUsers.length === 0) return true;

  // Auth required but no user
  if ((authRequired || requiredRoles.length > 0 || visibleToUsers.length > 0) && !userId) return false;

  // Per-user whitelist takes priority
  if (visibleToUsers.length > 0 && userId && visibleToUsers.includes(userId)) return true;

  // Role check
  if (requiredRoles.length > 0) {
    if (!userRoles || userRoles.length === 0) return false;
    return requiredRoles.some((r) => userRoles.includes(r));
  }

  // Auth required and user is logged in
  if (authRequired && userId) return true;

  return visibleToUsers.length === 0;
}

/** Build hierarchical nav from flat menu_items rows */
function buildNavFromDb(rows: any[], userId?: string, userRoles?: string[]): NavItem[] {
  const visible = rows.filter((r: any) => isItemVisible(r, userId, userRoles));
  const topLevel = visible.filter((r: any) => !r.parent_id).sort((a: any, b: any) => (a.display_order ?? 0) - (b.display_order ?? 0));
  return topLevel.map((parent: any) => {
    const kids = visible
      .filter((r: any) => r.parent_id === parent.id)
      .sort((a: any, b: any) => (a.display_order ?? 0) - (b.display_order ?? 0));
    if (kids.length > 0) {
      return {
        label: parent.label,
        children: kids.map((k: any) => ({
          label: k.label,
          href: k.action === "tow-guide" ? "/tow-guides" : k.url,
        })),
      };
    }
    return {
      label: parent.label,
      href: parent.action === "tow-guide" ? "/tow-guides" : parent.url,
    };
  });
}

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState<string | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { user, signOut } = useCustomerAuth();
  const dropdownTimeout = useRef<number | null>(null);
  const headerRef = useRef<HTMLElement>(null);
  const headerHeightRef = useRef("");
  const interactionReady = useInteractionReady();

  // Measure header height and expose as CSS custom property (debounced)
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    let timer: number | null = null;
    const ro = new ResizeObserver(([entry]) => {
      if (!entry) return;
      const nextRounded = Math.round(entry.contentRect.height);
      const prevRounded = parseInt(headerHeightRef.current) || 0;
      // Suppress sub-pixel jitter (font metrics, ResizeObserver loop) that
      // would otherwise reflow <main>'s padding-top and cause CLS.
      if (Math.abs(nextRounded - prevRounded) < 2) return;
      const nextHeight = `${nextRounded}px`;
      if (timer) window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        if (headerHeightRef.current === nextHeight) return;
        headerHeightRef.current = nextHeight;
        document.documentElement.style.setProperty("--header-h", nextHeight);
      }, 100);
    });
    ro.observe(el);
    return () => {
      if (timer) window.clearTimeout(timer);
      ro.disconnect();
    };
  }, []);

  // Suppress benign ResizeObserver loop notifications globally (one-time).
  useEffect(() => {
    const w = window as any;
    if (w.__roLoopSuppressed) return;
    w.__roLoopSuppressed = true;
    const handler = (e: ErrorEvent) => {
      if (e.message && e.message.includes("ResizeObserver loop")) {
        e.stopImmediatePropagation();
        e.preventDefault();
      }
    };
    window.addEventListener("error", handler);
    return () => window.removeEventListener("error", handler);
  }, []);

  const { data: isSuperAdmin } = useQuery({
    queryKey: ["is-super-admin", user?.id],
    queryFn: async () => {
      const supabase = await getSupabase();
      const { data } = await supabase.rpc("has_role", { _user_id: user!.id, _role: "super_admin" as any });
      return !!data;
    },
    enabled: !!user && interactionReady,
    staleTime: 5 * 60_000,
  });

  // Fetch user roles for menu permission filtering
  const { data: userRoles } = useQuery({
    queryKey: ["user-roles", user?.id],
    queryFn: async () => {
      const supabase = await getSupabase();
      const { data } = await supabase
        .from("user_roles" as any)
        .select("role")
        .eq("user_id", user!.id);
      return (data ?? []).map((r: any) => r.role as string);
    },
    enabled: !!user && interactionReady,
    staleTime: 5 * 60_000,
  });

  const accountPath = isSuperAdmin ? "/admin" : "/my-account";

  // Wishlist count badge for logged-in customers
  const { data: wishlistCount } = useQuery({
    queryKey: ["wishlist-count", user?.id],
    queryFn: async () => {
      const supabase = await getSupabase();
      const { count } = await supabase
        .from("wishlists")
        .select("id", { count: "exact", head: true })
        .eq("user_id", user!.id);
      return count || 0;
    },
    enabled: !!user && interactionReady,
    staleTime: 60_000,
  });

  // Pages with hero sliders where header should be fully transparent until scrolled
  const transparentHeroPages = ["/blog", "/nc-puma-dealer"];
  const isTransparentHero =
    transparentHeroPages.includes(location.pathname) ||
    location.pathname.startsWith("/p/");

  // Pages with a dark hero behind the transparent header — hamburger stays white there.
  // Everything else is treated as light background, so the hamburger needs a dark color.
  const darkHeroPages = ["/", "/blog", "/nc-puma-dealer"];
  const isDarkHero =
    darkHeroPages.includes(location.pathname) ||
    location.pathname.startsWith("/p/");
  const onLightBg = !scrolled && !isDarkHero;
  const transparentHeader = isTransparentHero && !scrolled;

  const { data: dbMenuItems } = useQuery({
    queryKey: ["menu_items"],
    queryFn: async () => {
      const supabase = await getSupabase();
      const { data } = await supabase
        .from("menu_items")
        .select("*")
        .eq("is_visible", true)
        .order("display_order");
      return data;
    },
    enabled: interactionReady,
    staleTime: 5 * 60_000,
  });

  const navItems: NavItem[] = dbMenuItems && dbMenuItems.length > 0
    ? buildNavFromDb(dbMenuItems, user?.id, userRoles ?? undefined)
    : fallbackNavItems;

  // Stable key — re-attach observers only when menu structure actually changes.
  const navKey = useMemo(() => navItems.map((i) => i.label).join("|"), [navItems]);

  const { data: headerSettings } = useQuery({
    queryKey: ["page-content", "global", "header"],
    queryFn: async () => {
      const supabase = await getSupabase();
      const { data, error } = await supabase
        .from("page_content")
        .select("content")
        .eq("page_slug", "global")
        .eq("section_key", "header")
        .maybeSingle();
      if (error) throw error;
      return (data?.content ?? null) as Record<string, any> | null;
    },
    enabled: interactionReady,
    staleTime: 5 * 60_000,
  });

  // Batch-fetch logo + favicon from site_settings in one query
  const { data: siteSettingsBatch } = useQuery({
    queryKey: ["site-settings-batch", "site_logo_url", "site_favicon_url"],
    queryFn: async () => {
      const supabase = await getSupabase();
      const { data } = await supabase
        .from("site_settings")
        .select("key, value")
        .in("key", ["site_logo_url", "site_favicon_url"]);
      const map: Record<string, string> = {};
      (data ?? []).forEach((r: any) => { if (r.value) map[r.key] = r.value as string; });
      return map;
    },
    enabled: interactionReady,
    staleTime: 5 * 60_000,
  });

  const siteLogoSetting = siteSettingsBatch?.['site_logo_url'] || "";
  const siteFaviconUrl = siteSettingsBatch?.['site_favicon_url'] || "";

  // Apply favicon dynamically
  useEffect(() => {
    if (!siteFaviconUrl) return;
    let link = document.querySelector("link[rel='icon']") as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = siteFaviconUrl;
  }, [siteFaviconUrl]);

  const h = headerSettings || {};
  // Default to the preloaded static logo. Only swap to a DB-stored URL when
  // it actually differs — prevents a second decode + sub-pixel reflow that
  // shows up as ~0.05 CLS on mobile.
  const dbLogo = siteLogoSetting || h['logo_url'] || "";
  const isDefaultLogo =
    !dbLogo ||
    dbLogo === "/images/new-logo.png" ||
    dbLogo === cdnImg("/images/new-logo.png") ||
    dbLogo === STATIC_ASSETS.logo200 ||
    dbLogo === STATIC_ASSETS.logo400 ||
    dbLogo.startsWith("/images/splash-assets/logo-");
  const logoUrl = isDefaultLogo ? STATIC_ASSETS.logo400 : dbLogo;
  const phone = h['phone'] || "910-793-2894";
  const textPhone = h['text_phone'] || "18332405678";
  const address = h['address'] || "5625 Barbados Blvd, Castle Hayne, NC 28429";
  const showTextLink = h['show_text_link'] !== false;
  const headerHtml = h['header_html'] || "";
  const customCss = h['custom_css'] || "";

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1280 && mobileOpen) {
        setMobileOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [mobileOpen]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleMouseEnter = (label: string) => {
    if (dropdownTimeout.current) clearTimeout(dropdownTimeout.current);
    setDropdownOpen(label);
  };
  const handleMouseLeave = () => {
    dropdownTimeout.current = window.setTimeout(() => setDropdownOpen(null), 120);
  };

  const navButtonBase =
    "whitespace-nowrap rounded-full border px-4 py-1.5 text-sm font-medium transition-all duration-200";
  const navButtonIdle =
    "border-primary bg-secondary text-primary-foreground hover:bg-primary hover:text-primary-foreground";
  const navButtonActive = "border-primary bg-primary text-primary-foreground";

  return (
    <header
      ref={headerRef}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 min-h-[120px] sm:min-h-[110px] lg:min-h-[140px] ${
        scrolled ? "bg-secondary/95 backdrop-blur-md shadow-lg" : transparentHeader ? "bg-transparent" : "lg:bg-transparent"
      }`}
    >
      {customCss && <style dangerouslySetInnerHTML={{ __html: customCss }} />}

      {headerHtml && (
        <div className="header-custom-content" dangerouslySetInnerHTML={{ __html: sanitizeHtml(headerHtml) }} />
      )}

      {/* Announcement banner */}
      <AnnouncementBanner />

      {/* Top bar — transparent over dark heroes so the hero image shows through;
          solid background everywhere else. */}
      <div className={`transition-colors duration-300 ${transparentHeader ? "bg-transparent border-transparent text-primary-foreground" : "bg-background border-b border-border/50 text-foreground"}`}>
        <div className="container flex flex-col sm:flex-row items-center justify-between py-1.5 text-sm gap-1 sm:gap-0 h-[56px] sm:h-[36px] overflow-hidden">
          <span className={`min-w-0 max-w-full truncate whitespace-nowrap font-body font-medium text-xs sm:text-sm ${transparentHeader ? "text-primary-foreground" : "text-foreground"}`}>{address}</span>
          <div className="flex shrink-0 flex-nowrap items-center gap-3 sm:gap-4 justify-center">
            <a href={`tel:${phone}`} className={`flex items-center gap-1.5 font-semibold hover:underline text-xs sm:text-sm ${transparentHeader ? "text-primary-foreground" : ""}`}>
              <Phone className="h-3.5 w-3.5" />
              {phone}
            </a>
            {showTextLink && (
              <>
                <span className={`text-xs ${transparentHeader ? "text-primary-foreground/70" : "text-muted-foreground"}`}>or</span>
                <a href={`sms:${textPhone}`} className={`flex items-center gap-1.5 font-medium hover:underline text-xs sm:text-sm ${transparentHeader ? "text-primary-foreground" : ""}`}>
                  <MessageCircle className="h-3.5 w-3.5" />
                  Text Us
                </a>
              </>
            )}
            <span className={transparentHeader ? "text-primary-foreground/70" : "text-muted-foreground"}>|</span>
            {user ? (
              <div className="flex items-center gap-2">
                <Suspense fallback={null}>
                  <NotificationDropdown userId={user.id} />
                </Suspense>
                <Link
                  to="/my-account/wishlist"
                  className={`relative flex items-center gap-1 hover:underline text-xs ${transparentHeader ? "text-primary-foreground" : "text-foreground"}`}
                  aria-label="My Wishlist"
                >
                  <Heart className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">Wishlist</span>
                  {wishlistCount ? (
                    <span className="ml-0.5 inline-flex min-w-[1.1rem] items-center justify-center rounded-full bg-background text-primary text-[10px] font-bold leading-none px-1 py-0.5">
                      {wishlistCount}
                    </span>
                  ) : null}
                </Link>
                <Link to={accountPath} className={`flex items-center gap-1 hover:underline text-xs ${transparentHeader ? "text-primary-foreground" : "text-foreground"}`}><User className="h-3 w-3" />My Account</Link>
                <button onClick={signOut} className={`flex items-center gap-1 hover:underline text-xs ${transparentHeader ? "text-primary-foreground" : "text-foreground"}`}><LogOut className="h-3 w-3" />Logout</button>
                <HardRefresh />
              </div>
            ) : (
              <>
                <Link
                  to="/community-login?redirect=%2Fmy-account%2Fwishlist"
                  className={`flex items-center gap-1 hover:underline text-xs ${transparentHeader ? "text-primary-foreground" : "text-foreground"}`}
                  aria-label="Sign in to view your wishlist"
                >
                  <Heart className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">Wishlist</span>
                </Link>
                <Link to="/community-login" className={`flex items-center gap-1 hover:underline text-xs sm:text-sm ${transparentHeader ? "text-primary-foreground" : "text-foreground"}`}>
                  <LogIn className="h-3 w-3" /> Login / Sign Up
                </Link>
                <HardRefresh />
              </>
            )}
          </div>
        </div>
      </div>

      {/* Main nav */}
      <div className="container relative flex items-center justify-between gap-4 py-2 min-h-[80px] sm:min-h-[88px] lg:min-h-[104px] h-auto">
        <Link to="/" className="flex flex-shrink-0 items-center gap-2" onClick={() => { /* stays in app, gate won't re-show because hasVisited is set */ }}>
          <ResilientImg
            // Default logo: single URL (no srcset) so it byte-matches the
            // high-priority <link rel=preload> in __root and the LCP request
            // starts from the HTML instead of after hydration.
            src={logoUrl === "/images/new-logo.png" || logoUrl === cdnImg("/images/new-logo.png")
              ? STATIC_ASSETS.logo400
              : renderUrl(logoUrl, 240, 70)}
            {...(logoUrl === "/images/new-logo.png" || logoUrl === cdnImg("/images/new-logo.png")
              ? {}
              : {
                  srcSet: `${renderUrl(logoUrl, 160, 65)} 160w, ${renderUrl(logoUrl, 240, 70)} 240w, ${renderUrl(logoUrl, 320, 70)} 320w`,
                  sizes: "(max-width: 639px) 150px, (max-width: 1023px) 200px, 260px",
                })}
            alt="Rex & Sons RVs" width={250} height={75} decoding="async"
            loading="eager" fetchPriority="high"
            style={{ aspectRatio: "250 / 75" }}
            className="h-11 sm:h-16 lg:h-20 w-auto min-w-[120px] max-w-[150px] sm:max-w-[260px] lg:max-w-[300px] object-contain header-logo"
          />
        </Link>

        <nav
          className="hidden min-w-0 flex-1 items-center justify-center gap-1.5 flex-wrap min-h-[48px] xl:flex"
        >
          {navItems.map((item) =>
            item.children ? (
              <div
                key={item.label}
                className={`relative ${dropdownOpen === item.label ? "z-50" : "z-10"}`}
                onMouseEnter={() => handleMouseEnter(item.label)}
                onMouseLeave={handleMouseLeave}
              >
                <button
                  className={`${navButtonBase} ${
                    dropdownOpen === item.label ? navButtonActive : navButtonIdle
                  } flex items-center gap-1`}
                >
                  {item.label}
                  <ChevronDown className="h-3.5 w-3.5" />
                </button>
                {/* CSS-animated dropdown */}
                <div
                  className={`absolute left-0 top-full z-50 w-52 rounded-md border bg-card py-1 shadow-xl transition-all duration-150 origin-top ${
                    dropdownOpen === item.label
                      ? "opacity-100 scale-y-100 pointer-events-auto"
                      : "opacity-0 scale-y-95 pointer-events-none"
                  }`}
                >
                  {item.children.map((child) =>
                      <Link
                        key={child.label + child.href}
                        to={child.href}
                        className="block px-4 py-2 text-sm text-card-foreground hover:bg-muted"
                      >
                        {child.label}
                      </Link>
                  )}
                </div>
              </div>
            ) : (
              <Link
                key={item.href}
                to={item.href!}
                className={`${navButtonBase} ${
                  location.pathname === item.href ? navButtonActive : navButtonIdle
                }`}
              >
                {item.label}
              </Link>
            )
          )}
          <HeaderSearchInline />
        </nav>

        {/* Mobile toggle */}
        <div className="flex flex-shrink-0 items-center gap-2">
          <HeaderSearchMobile className={onLightBg ? "text-secondary" : "text-primary-foreground"} />
          <Button
            variant="ghost"
            size="icon"
            className={`h-14 w-14 min-h-[48px] min-w-[48px] xl:hidden [&_svg]:!size-9 ${
                onLightBg
                  ? "text-primary-foreground lg:text-secondary lg:hover:text-primary"
                  : "text-primary-foreground"
            }`}
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? "Close navigation menu" : "Open navigation menu"}
          >
            {mobileOpen ? <X className="h-9 w-9" /> : <Menu className="h-9 w-9" />}
          </Button>
        </div>
      </div>

      {/* Mobile menu — CSS transition instead of framer-motion */}

      <div
        className={`relative z-50 overflow-hidden border-t border-border/20 bg-secondary transition-[max-height] duration-300 ease-in-out xl:hidden ${mobileOpen ? "max-h-[80vh] overflow-y-auto" : "max-h-0"}`}
      >
        <nav className="container flex flex-col gap-1 py-4">
          {navItems.map((item) =>
            item.children ? (
              <div key={item.label}>
                <button
                  onClick={() => setMobileExpanded(mobileExpanded === item.label ? null : item.label)}
                  className="flex w-full items-center justify-between px-3 py-2.5 text-base font-semibold text-secondary-foreground/90"
                >
                  {item.label}
                  <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${mobileExpanded === item.label ? "rotate-180" : ""}`} />
                </button>
                {mobileExpanded === item.label && item.children.map((child) =>
                    <Link
                      key={child.label + child.href}
                      to={child.href}
                      onClick={() => setMobileOpen(false)}
                      className="block px-6 py-2.5 text-base text-secondary-foreground/80 hover:text-primary"
                    >
                      {child.label}
                    </Link>
                )}
              </div>
            ) : (
              <Link
                key={item.href}
                to={item.href!}
                onClick={() => setMobileOpen(false)}
                className="px-3 py-2.5 text-base font-medium text-secondary-foreground/80 hover:text-primary"
              >
                {item.label}
              </Link>
            )
          )}
          {/* Mobile auth */}
          <div className="border-t border-border/20 mt-2 pt-2">
            {user ? (
              <>
                <Link to={accountPath} onClick={() => setMobileOpen(false)} className="px-3 py-2.5 text-base text-secondary-foreground/80 hover:text-primary flex items-center gap-2">
                  <User className="h-5 w-5" /> My Account
                </Link>
                <button onClick={() => { signOut(); setMobileOpen(false); }} className="px-3 py-2.5 text-base text-secondary-foreground/80 hover:text-primary flex items-center gap-2">
                  <LogOut className="h-5 w-5" /> Logout
                </button>
              </>
            ) : (
              <Link to="/community-login" onClick={() => setMobileOpen(false)} className="px-3 py-2.5 text-base text-secondary-foreground/80 hover:text-primary flex items-center gap-2">
                <LogIn className="h-5 w-5" /> Login / Sign Up
              </Link>
            )}
          </div>
        </nav>
      </div>

    </header>
  );
};

export default Header;
